import { DepartmentDto, OrganizationDto } from "@dialogics/shared/dto/types"
import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { useProfileQuery } from "../features/auth/service"
import { useGetDepartmentsQuery } from "@/features/department/department-service.ts"

// ─── types ────────────────────────────────────────────────────────────────────

// ─── localStorage helpers ─────────────────────────────────────────────────────

const ORG_KEY = "selectedOrganization"
const DEPT_KEY = "selectedDepartment"

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function writeJson<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value))
}

function deleteJson(key: string): void {
  localStorage.removeItem(key)
}

// ─── context ──────────────────────────────────────────────────────────────────

interface SelectionContextType {
  currentOrganization: OrganizationDto | null
  setCurrentOrganization: (org: OrganizationDto) => void
  selectedDepartment: DepartmentDto | null
  setSelectedDepartment: (dept: DepartmentDto) => void
  departments: DepartmentDto[]
  clearSelection: () => void
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined)

export function SelectionProvider({ children }: { children: ReactNode }) {
  const profile = useProfileQuery()

  // Оба стейта инициализируются сразу из localStorage — одинаковый подход
  const [currentOrganization, setCurrentOrganizationState] = useState<OrganizationDto | null>(() =>
    readJson<OrganizationDto>(ORG_KEY),
  )
  const [selectedDepartment, setSelectedDepartmentState] = useState<DepartmentDto | null>(() =>
    readJson<DepartmentDto>(DEPT_KEY),
  )

  const { data: departmentList } = useGetDepartmentsQuery(
    {
      organizationId: profile?.data?.organization?.id || currentOrganization?.id,
    },
    {
      skip: !profile?.data?.organization && !currentOrganization,
    },
  )
  // После загрузки профиля — валидируем / обновляем департамент из свежих данных
  useEffect(() => {
    if (!departmentList?.results?.length) return

    const current = selectedDepartment
    const fresh = current ? departmentList.results.find(d => d.id === current.id) : null

    if (fresh) {
      // Обновляем объект если данные поменялись на сервере
      const changed = fresh.title !== current!.title || fresh.call_data_source_id !== current!.call_data_source_id
      if (changed) {
        setSelectedDepartmentState(fresh)
        writeJson(DEPT_KEY, fresh)
      }
    } else {
      // Сохранённый департамент больше недоступен — берём первый
      setSelectedDepartmentState(departmentList.results[0])
      writeJson(DEPT_KEY, departmentList.results[0])
    }
  }, [departmentList?.results, selectedDepartment])

  const handleSetCurrentOrganization = (org: OrganizationDto) => {
    setCurrentOrganizationState(org)
    writeJson(ORG_KEY, org)
  }

  const handleSetSelectedDepartment = (dept: DepartmentDto) => {
    setSelectedDepartmentState(dept)
    writeJson(DEPT_KEY, dept)
  }

  const clearSelection = () => {
    setCurrentOrganizationState(null)
    setSelectedDepartmentState(null)
    deleteJson(ORG_KEY)
    deleteJson(DEPT_KEY)
  }

  return (
    <SelectionContext.Provider
      value={{
        currentOrganization,
        setCurrentOrganization: handleSetCurrentOrganization,
        selectedDepartment,
        setSelectedDepartment: handleSetSelectedDepartment,
        departments: departmentList?.results || [],
        clearSelection,
      }}
    >
      {children}
    </SelectionContext.Provider>
  )
}

export function useSelection() {
  const context = useContext(SelectionContext)
  if (context === undefined) {
    throw new Error("useSelection must be used within a SelectionProvider")
  }
  return context
}
